import {
  ButtonColor,
  ButtonSize,
  Checkbox,
  Combobox,
  CurrencyInput,
  Loader,
  Modal,
  ModalType,
  Option,
  TextInput,
} from '@code/innovance-react-ui-kit';
import { yupResolver } from '@hookform/resolvers';
import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
  CardReissue,
  Document,
  DocumentStorage,
  ProductOperationsReissue,
} from 'src/front/api/apiInstance';

import {
  OperationsReissueGetResponse,
  ReferenceDataType,
  UploadType,
} from 'src/front/api/generated';

import usePRM from 'src/front/common/hooks/usePRM';
import displayPDF from 'src/front/common/utils/domUtils';

import {
  dictionaryLineFormat,
  formatBlank,
  formatEmbossName,
  formatNumeric,
  optionFormat,
} from 'src/front/common/utils/formUtils';
import { CardReissueSchema } from 'src/front/common/validations/cardReissue';
import AsyncButton from 'src/front/components/AsyncButton';
import UploadDocumentModal, {
  UploadDocumentFormData,
} from 'src/front/components/document/UploadDocumentModal';

import {
  ButtonListRow,
  Container,
  FormElement,
  FormRow,
  InfoLabel,
  InfoRow,
  Separator,
  Subtitle,
  Title,
  WarningText,
} from 'src/front/components/FormStyles';
import Markdown from 'src/front/components/Markdown';
import SuccessButtonText from 'src/front/components/SuccessButtonText';

import {
  useCardReissueState,
  useCardSearchDispatch,
  useClearDispatch,
  useCustomerSearchDispatch,
  useSetDocumentsDispatch,
  useSetReasonsDispatch,
} from 'src/front/contexts/CardReissueContext';

import UploadDocument from 'src/front/pages/applications/upload-document/components/UploadDocument';
import styled from 'styled-components';

const StyledButton = styled(AsyncButton)`
  width: 300px;
  padding-left: 40px;

  text-align: left;
`;

interface CardReissueFormData {
  cardStatus: Option | null;
  cardSubStatus: Option | null;
  cardReasonForReissue: Option | null;
  embossedName: string;
  embossedNameOther: string;
  isCardDigital: boolean;
  isNoNameCard: boolean;
  noNameCardId: string;
  lastFourDigits: string;
  monthlyIncome: string;
  proofOfIncome: boolean;
  isCampaignFlag: boolean;
  showIncomeLabels: boolean;
}

const defaultValues = {
  cardStatus: null,
  cardSubStatus: null,
  cardReasonForReissue: null,
  embossedName: '',
  embossedNameOther: '',
  isCardDigital: false,
  isNoNameCard: false,
  noNameCardId: '',
  lastFourDigits: '',
  monthlyIncome: '',
  proofOfIncome: false,
  isCampaignFlag: true,
};

interface Props {
  goToFirstStep: () => void;
  isEdit?: boolean;
  operationsReissueData?: OperationsReissueGetResponse;
  referenceNumber?: string;
  setIsCompleted?: (value: boolean) => void;
  showIncomeLabels: boolean;
}

const CardReissueIssue: FC<Props> = ({
  goToFirstStep,
  operationsReissueData,
  referenceNumber,
  isEdit = false,
  setIsCompleted,
  showIncomeLabels,
}) => {
  const {
    documents,
    customerSearchValues,
    cardSearchValues,
    cardReissueValues,
  } = useCardReissueState();

  const { customerNumber, clientName } = customerSearchValues;

  const { pos, branch, cardListType, selectedCard } = cardSearchValues;

  const { t } = useTranslation();

  const documentDispatch = useSetDocumentsDispatch();
  const reasonsDispatch = useSetReasonsDispatch();
  const clearSearchInputs = useClearDispatch();
  const customerSearchDispatch = useCustomerSearchDispatch();
  const cardSearchDispatch = useCardSearchDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasNoDocumentWarning, setHasNoDocumentWarning] = useState<boolean>(false);
  const [showDocumentUploadForm, setShowDocumentUploadForm] = useState<boolean>(
    Boolean(cardReissueValues.cardReasonForReissue) || false
  );
  const [showIncomeDocumentModal, setShowIncomeDocumentModal] = useState<boolean>(false);
  const [isApplicationCompleted, setIsApplicationCompleted] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [emmbossedNameLength, setEmmbossedNameLength] = useState<number>(21);
  const [isConsentVisible, setIsConsentVisible] = useState<boolean>(false);
  const cardStatuses = usePRM(ReferenceDataType.REISSUECARDSTATUS);
  const cardSubStatuses = usePRM(ReferenceDataType.REISSUECARDSUBSTATUS);
  const cardReissueReasons = usePRM(ReferenceDataType.REISSUEREASONS);
  const documentTypes = usePRM(ReferenceDataType.REISSUEDOCUMENTTYPE);

  const {
    watch,
    register,
    errors,
    control,
    getValues,
    setValue,
    handleSubmit,
    formState,
  } = useForm<CardReissueFormData>({
    defaultValues: {
      ...defaultValues,
      cardStatus: cardReissueValues.cardStatus,
      cardSubStatus: cardReissueValues.cardSubStatus,
      cardReasonForReissue: cardReissueValues.cardReasonForReissue,
      embossedName: cardReissueValues.embossedName || selectedCard?.embossedName || '',
      embossedNameOther:
        cardReissueValues.embossedNameOther || selectedCard?.embossedNameOther || '',
      isNoNameCard: cardReissueValues.isNoNameCard,
      showIncomeLabels,
    },
    resolver: yupResolver(CardReissueSchema),
  });

  const handleTextChange = (fieldName: string) => (text: string) =>
    setValue(fieldName, text, {
      shouldValidate: formState.isSubmitted || !!text,
      shouldDirty: formState.isSubmitted || !!text,
    });

  const getFundamentalInfo = useCallback(async (): Promise<void> => {
    const cardInfoResponse = await CardReissue.getFundamentalInfoForReissue({
      cardId: selectedCard?.id || '',
      customerNumber,
    });
    setEmmbossedNameLength(cardInfoResponse.data.cardEmbossedNameMaxLength);
    setIsConsentVisible(cardInfoResponse.data.isCardNSPKConcentVisible || false);
  }, [customerNumber, selectedCard]);

  useEffect(() => {
    register({ name: 'cardStatus' });
    register({ name: 'isNoNameCard' });
    register({ name: 'noNameCardId' });
    register({ name: 'isCampaignFlag' });
    register({ name: 'lastFourDigits' });
    register({ name: 'cardReasonForReissue' });
    register({ name: 'isCardDigital' });
    register({ name: 'monthlyIncome' });
    register({ name: 'proofOfIncome' });
    register({ name: 'isCampaignFlag' });
    register({ name: 'showIncomeLabels' });
  }, [register]);

  useEffect(() => {
    getFundamentalInfo();
  }, [getFundamentalInfo]);

  useEffect(() => {
    if (
      isEdit &&
      operationsReissueData &&
      cardStatuses.length &&
      cardSubStatuses.length &&
      cardReissueReasons.length &&
      !customerNumber &&
      !selectedCard
    ) {
      const cardStatus =
        cardStatuses.find((status) => status.value === operationsReissueData.cardStatusCode) ||
        null;
      const cardSubStatus =
        cardSubStatuses.find(
          (status) => status.value === operationsReissueData.cardSubStatusCode
        ) || null;
      const cardReissueReason =
        cardReissueReasons.find(
          (reason) => reason.value === operationsReissueData.reissueReasonCode
        ) || null;

      [
        { name: 'cardStatus', value: cardStatus },
        { name: 'cardSubStatus', value: cardSubStatus },
        { name: 'cardReasonForReissue', value: cardReissueReason },
        { name: 'embossedName', value: operationsReissueData.embossedName },
        { name: 'embossedNameOther', value: operationsReissueData.embossedNameOther },
        { name: 'isNoNameCard', value: !!operationsReissueData.noNameCardId },
        { name: 'noNameCardId', value: operationsReissueData.noNameCardId },
      ].forEach(({ name, value }) => setValue(name, value as {} | undefined));

      documentDispatch({
        documents:
          operationsReissueData.documentUploadDataList.map((document) => ({
            documentType: optionFormat(document.documentType),
            explanation: document.explanation || '',
            isOldDocument: document.isOldDocument,
            documentId: document.documentId,
            createDate: document.createDate,
            filePath: '',
            documentName: document.documentName,
          })) || [],
      });

      setShowDocumentUploadForm(!!operationsReissueData.reissueReasonCode);

      const emptyValue = {} as any;
      customerSearchDispatch({
        customerSearchFormValues: emptyValue,
        customerSearchValues: {
          clientName: operationsReissueData.clientName,
          customerNumber: operationsReissueData.customerNumber,
          customerId: operationsReissueData.customerId,
          customerPageNumber: 1,
          selectedCustomer: emptyValue,
        },
      });

      cardSearchDispatch({
        cardSearchValues: {
          branch: emptyValue,
          cardListType: emptyValue,
          cardPageNumber: 1,
          pos: emptyValue,
          selectedCard: {
            ...emptyValue,
            id: operationsReissueData.cardId,
            cardNumber: operationsReissueData.cardNumber,
            expireDate: operationsReissueData.expireDate,
            cardAccountNumber: operationsReissueData.cardAccountNumber,
          },
        },
      });
    }
  }, [
    isEdit,
    operationsReissueData,
    cardStatuses,
    setValue,
    cardSubStatuses,
    cardReissueReasons,
    customerSearchDispatch,
    customerNumber,
    selectedCard,
    cardSearchDispatch,
    documentDispatch,
  ]);

  useEffect(() => {
    if (cardListType?.value === 'DIGITAL') {
      setValue('isCardDigital', true);
      if (selectedCard) {
        const cardStatus =
          cardStatuses.find((status) => status.value === selectedCard.cardStatusCode) || null;
        const cardSubStatus =
          cardSubStatuses.find((status) => status.value === selectedCard.cardSubStatusCode) || null;
        const cardReissueReason =
          cardReissueReasons.find((reason) => reason.value === selectedCard.reissueReasonCode) ||
          null;
        [
          { name: 'cardStatus', value: cardStatus },
          { name: 'cardSubStatus', value: cardSubStatus },
          { name: 'cardReasonForReissue', value: cardReissueReason },
          { name: 'embossedName', value: selectedCard.embossedName },
          { name: 'embossedNameOther', value: selectedCard.embossedNameOther },
          { name: 'isNoNameCard', value: selectedCard.isNoNameChecked },
        ].forEach(({ name, value }) => setValue(name, value as {} | undefined));
      }
    }
  }, [
    cardListType,
    cardReissueReasons,
    cardSearchValues,
    cardStatuses,
    cardSubStatuses,
    selectedCard,
    setValue,
  ]);

  const handleContinue = async (): Promise<void> => {
    if (!documents?.length) {
      setHasNoDocumentWarning(true);
    } else {
      setIsLoading(true);
      try {
        const {
          cardStatus,
          cardSubStatus,
          cardReasonForReissue,
          embossedName,
          embossedNameOther,
          isNoNameCard,
          noNameCardId,
          lastFourDigits,
          monthlyIncome,
          proofOfIncome,
          isCampaignFlag,
        } = getValues();

        let successResponseMessage = '';

        if (isEdit) {
          const {
            data: response,
          } = await ProductOperationsReissue.updateReissueDataByReferenceNumber({
            customerNumber,
            cardId: selectedCard?.id || '',
            cardStatusCode: cardStatus?.value || '',
            cardSubStatusCode: cardSubStatus?.value || '',
            reissueReasonCode: cardReasonForReissue?.value || '',
            embossedName,
            documentUploadDataList: documents.map((document) => ({
              ...document,
              documentType: dictionaryLineFormat(document.documentType),
            })),
            referenceNumber: referenceNumber || '',
            cardAccountNumber: selectedCard?.cardAccountNumber || '',
            cardNumber: selectedCard?.cardNumber || '',
            lastFourDigits,
            noNameCardId: selectedCard?.noNameCardId || '',
          });
          if (response.result === 'OK') {
            successResponseMessage = response.reissueResultMessage;
          }
        } else if (cardListType?.value === 'DIGITAL') {
          const { data: response } = await CardReissue.applyDigitalCardReissue({
            customerNumber,
            cardId: selectedCard?.id || '',
            clientName,
            posId: pos?.value || '',
            ...(isNoNameCard
              ? {
                  noNameCardId,
                  lastFourDigits,
                }
              : {}),
            documentUploadDataList: documents.map((document) => ({
              ...document,
              documentType: dictionaryLineFormat(document.documentType),
            })),
          });
          if (response.result === 'OK') {
            successResponseMessage = t('cardReissue_digitalCardsReissue_successMessage');
          }
        } else {
          const { data: response } = await CardReissue.applyCardReissue({
            customerNumber,
            customerTypeCode: branch?.value || '',
            cardNumber: selectedCard?.cardNumber || '',
            isCampaignFlag: isConsentVisible ? isCampaignFlag : undefined,
            cardId: selectedCard?.id || '',
            cardStatusCode: cardStatus?.value || '',
            cardSubStatusCode: cardSubStatus?.value || '',
            reissueReasonCode: cardReasonForReissue?.value || '',
            clientName,
            embossedName,
            embossedNameOther,
            mainMonthlyIncome: monthlyIncome ? Number(formatBlank(monthlyIncome)) : undefined,
            proofOfIncomeProvided: proofOfIncome,
            posId: pos?.value || '',
            ...(isNoNameCard
              ? {
                  noNameCardId,
                  lastFourDigits,
                }
              : {}),
            documentUploadDataList: documents.map((document) => ({
              ...document,
              documentType: dictionaryLineFormat(document.documentType),
            })),
          });
          if (response.result === 'OK') {
            successResponseMessage = response.reissueResultMessage;
          }
        }

        if (successResponseMessage) {
          setSuccessMessage(successResponseMessage);
          setIsApplicationCompleted(true);
          setIsCompleted && setIsCompleted(true);
        }
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    }
  };

  const handleNoNameCard = ({ target }: ChangeEvent<HTMLInputElement>): void => {
    showDocumentUploadForm && setShowDocumentUploadForm(false);
    setDocuments([]);
    setValue('proofOfIncome', false);
    setValue('isNoNameCard', target.checked);
  };
  const handleProofOfIncome = ({ target }: ChangeEvent<HTMLInputElement>): void => {
    setShowIncomeDocumentModal(target.checked);
    setValue('proofOfIncome', target.checked);
  };
  const handleApplicationSuccessClick = (): void => {
    setIsApplicationCompleted(false);
    clearSearchInputs();
    goToFirstStep();
    setIsCompleted && setIsCompleted(false);
  };

  const handleStatusChange = async (status: Option | null): Promise<void> => {
    setShowDocumentUploadForm(false);
    setDocuments([]);
    setValue('proofOfIncome', false);
    setValue('cardStatus', status);
  };

  const handleReasonChange = async (reason: Option | null): Promise<void> => {
    setShowDocumentUploadForm(false);
    setDocuments([]);
    setValue('proofOfIncome', false);
    setValue('cardReasonForReissue', reason);
  };

  const onSubmit = async (formData: CardReissueFormData): Promise<void> => {
    try {
      const {
        cardStatus,
        cardSubStatus,
        cardReasonForReissue,
        embossedName,
        embossedNameOther,
        isNoNameCard,
        noNameCardId,
        lastFourDigits,
        isCampaignFlag,
      } = formData;
      const { data } = await CardReissue.generateCardReissueForm({
        cardNumber: selectedCard?.cardNumber || '',
        customerTypeCode: branch?.value || '',
        isCampaignFlag: isConsentVisible ? isCampaignFlag : undefined,
        cardId: selectedCard?.id || '',
        cardAccountNumber: selectedCard?.cardAccountNumber || '',
        clientName,
        customerNumber,
        reissueReasonCode: cardReasonForReissue?.value || '',
        reissueReasonValue: cardReasonForReissue?.label || '',
        currency: selectedCard?.currency || '',
        cardStatusValue: cardStatus?.label || '',
        noNameCardId: isEdit ? operationsReissueData?.noNameCardId : selectedCard?.noNameCardId,
      });
      if (data.result === 'OK') {
        const { data: response } = await DocumentStorage.getDocument({
          documentId: data.documentId,
        });
        if (response.result === 'OK') {
          setShowDocumentUploadForm(true);
          displayPDF(response.documentData);
          reasonsDispatch({
            cardReissueValues: {
              cardStatus,
              cardSubStatus,
              cardReasonForReissue,
              embossedName,
              embossedNameOther,
              isNoNameCard,
              noNameCardId,
              lastFourDigits,
            },
          });
        }
      }
    } catch (err) {
      // TODO (mertohancan): show error on modal or handle with error boundary
    }
  };

  const setDocuments = (docs: UploadDocumentFormData[]): void =>
    documentDispatch({ documents: docs });
  const handleIncomeDocumentSubmit = async (formData: UploadDocumentFormData): Promise<void> => {
    let newDocuments;
    if (!formData.documentId && formData.documentType) {
      const { data } = await Document.uploadDocuments(
        formData.files && formData.files[0],
        formData.documentType.value,
        UploadType.DOCUMENTUPLOAD,
        formData.explanation,
        customerSearchValues.customerId
      );
      if (data.result === 'OK') {
        newDocuments = [
          {
            documentType: (documentTypes &&
              documentTypes.find((type) => type.value === data.documentType)) || {
              value: data.documentType,
              label: data.documentType,
            },
            explanation: data.explanation,
            isOldDocument: data.isOldDocument,
            documentId: data.documentId,
            createDate: data.createDate,
            filePath: formData.filePath,
            documentName: data.documentName,
          },
          ...(documents || []),
        ];
      }
    } else if (formData.documentType) {
      const { data } = await Document.updateDocument({
        documentId: formData.documentId,
        explanation: formData.explanation,
        documentType: formData.documentType.value,
        documentName: formData.documentName,
        uploadType: UploadType.DOCUMENTUPLOAD,
      });
      if (data.result === 'OK') {
        newDocuments = documents?.map((doc) =>
          doc.documentId === formData.documentId
            ? {
                documentType: (documentTypes &&
                  documentTypes.find((type) => type.value === data.documentType)) || {
                  value: data.documentType,
                  label: data.documentType,
                },
                explanation: data.explanation,
                isOldDocument: data.isOldDocument,
                documentId: data.documentId,
                createDate: data.createDate,
                filePath: formData.filePath,
                documentName: data.documentName,
              }
            : doc
        );
      }
    }
    setDocuments(newDocuments || []);
    setShowIncomeDocumentModal(false);
  };
  return (
    <Container>
      {isApplicationCompleted ? (
        <Modal
          description={<Markdown text={successMessage} />}
          onCancel={handleApplicationSuccessClick}
          submitText={t('general_components_close_button')}
          title={t('general_wellDone_text')}
          type={ModalType.Success}
        />
      ) : null}
      <Title>
        <Trans i18nKey="Card Reissue" />
      </Title>

      <InfoRow>
        <InfoLabel>
          <Trans i18nKey="cardReissue_clientName_text" />:
        </InfoLabel>
        {clientName}
      </InfoRow>
      <InfoRow>
        <InfoLabel>
          <Trans i18nKey="cardReissue_cardNumber_text" />:
        </InfoLabel>
        {selectedCard?.cardNumber}
      </InfoRow>
      <InfoRow>
        <InfoLabel>
          <Trans i18nKey="cardReissue_accountNumber_text" />:
        </InfoLabel>
        {selectedCard?.cardAccountNumber}
      </InfoRow>
      <InfoRow>
        <InfoLabel>
          <Trans i18nKey="cardReissue_cardExpireDate_text" />:
        </InfoLabel>
        {selectedCard?.expireDate}
      </InfoRow>
      {!isEdit ? (
        <InfoRow>
          <InfoLabel>
            <Trans i18nKey="cardReissue_cardListType_label" />:
          </InfoLabel>
          {cardListType?.label}
        </InfoRow>
      ) : null}

      <Separator />
      <form onSubmit={handleSubmit(onSubmit)}>
        {isLoading ? <Loader /> : null}
        <FormRow>
          <FormElement>
            <Combobox
              disabled={watch('isCardDigital')}
              errorMessage={t(errors?.cardStatus?.message as string)}
              items={cardStatuses}
              label={t('cardReissue_cardStatus_label')}
              name="cardStatus"
              onChange={handleStatusChange}
              value={watch('cardStatus')}
              required
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="cardSubStatus"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={watch('isCardDigital')}
                  errorMessage={t(errors?.cardSubStatus?.message as string)}
                  items={cardSubStatuses}
                  label={t('cardReissue_cardSubstatus_label')}
                  name="cardSubStatus"
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <Combobox
              disabled={watch('isCardDigital')}
              errorMessage={t(errors?.cardReasonForReissue?.message as string)}
              items={cardReissueReasons}
              label={t('cardReissue_reasonForReissue_label')}
              name="cardReasonForReissue"
              onChange={handleReasonChange}
              value={watch('cardReasonForReissue')}
              required
            />
          </FormElement>
        </FormRow>

        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="embossedName"
              render={({ onChange, value }) => (
                <TextInput
                  disabled={watch('isCardDigital')}
                  errorMessage={t(errors?.embossedName?.message as string)}
                  formatter={formatEmbossName}
                  label={t('cardReissue_embossedName_text')}
                  maxLength={emmbossedNameLength}
                  name="embossedName"
                  onChange={onChange}
                  value={value}
                  required
                />
              )}
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="embossedNameOther"
              render={({ onChange, value }) => (
                <TextInput
                  disabled={watch('isCardDigital')}
                  errorMessage={t(errors?.embossedNameOther?.message as string)}
                  formatter={formatEmbossName}
                  label={t('cardReissue_embossedName2_label')}
                  maxLength={emmbossedNameLength}
                  name="embossedNameOther"
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <Checkbox
              disabled={isEdit || watch('isCardDigital')}
              id="isNoNameCard"
              label={t('cardReissue_noNameCard_text')}
              onChange={handleNoNameCard}
              value={watch('isNoNameCard')}
            />
          </FormElement>
        </FormRow>
        {isConsentVisible ? (
          <FormRow>
            <FormElement>
              <Checkbox
                disabled={isEdit}
                id="isCampaignFlag"
                label={t('cardReissue_mirLoyalty_text')}
                onChange={({ target }) => setValue('isCampaignFlag', target.checked)}
                value={watch('isCampaignFlag')}
              />
            </FormElement>
          </FormRow>
        ) : null}
        {watch('isNoNameCard') ? (
          <FormRow>
            <FormElement>
              <TextInput
                errorMessage={t(errors?.noNameCardId?.message as string)}
                formatter={formatNumeric}
                label={t('cardReissue_uniqueCardCode_label')}
                maxLength={10}
                name="noNameCardId"
                onChange={handleTextChange('noNameCardId')}
                value={watch('noNameCardId')}
                required
              />
            </FormElement>
            <FormElement>
              <TextInput
                errorMessage={t(errors?.lastFourDigits?.message as string)}
                formatter={formatNumeric}
                label={t('cardReissue_Last4Digits_label')}
                maxLength={4}
                name="lastFourDigits"
                onChange={handleTextChange('lastFourDigits')}
                value={watch('lastFourDigits')}
                required
              />
            </FormElement>
          </FormRow>
        ) : null}
        {showIncomeLabels ? (
          <>
            <FormRow>
              <FormElement>
                <Controller
                  control={control}
                  name="monthlyIncome"
                  render={({ onChange, value }) => (
                    <CurrencyInput
                      currencySymbol="RUB"
                      errorMessage={t(errors?.monthlyIncome?.message as string)}
                      label={t('cardReissue_monthlyIncome_label')}
                      maxLength={10}
                      onChange={onChange}
                      value={value}
                      required
                    />
                  )}
                />
              </FormElement>
            </FormRow>
            <FormRow>
              <FormElement>
                <Checkbox
                  id="proofOfIncome"
                  label={t('cardReissue_proofOfIncome_label')}
                  onChange={handleProofOfIncome}
                  value={watch('proofOfIncome')}
                />
              </FormElement>
            </FormRow>
          </>
        ) : null}

        {watch('cardStatus') && watch('cardReasonForReissue') ? (
          <>
            <Separator />
            <Title>
              <Trans i18nKey="cardReissue_downloadCardReissueForm_text" />
            </Title>
            <ButtonListRow>
              <StyledButton
                color={ButtonColor.Secondary}
                text={
                  <SuccessButtonText
                    isSuccess={showDocumentUploadForm}
                    label={t('general_download_button')}
                  />
                }
                type="submit"
              />
            </ButtonListRow>
          </>
        ) : null}
      </form>
      {showIncomeDocumentModal ? (
        <UploadDocumentModal
          documentTypes={[
            documentTypes.find((element) => element.value === 'IS') || {
              value: '',
              label: '',
            },
          ]}
          onClose={() => setShowIncomeDocumentModal(false)}
          submit={handleIncomeDocumentSubmit}
        />
      ) : null}
      {showDocumentUploadForm ? (
        <>
          <Separator />
          <Subtitle>
            <Trans i18nKey="uploadDocuments_uploadDocument_subtitle" />
          </Subtitle>
          <UploadDocument
            customerId={customerSearchValues.customerId}
            documents={documents || []}
            documentTypes={documentTypes}
            isLoading={false}
            setDocuments={setDocuments}
            uploadType={UploadType.REISSUE}
          />
          {hasNoDocumentWarning && !documents?.length ? (
            <WarningText>
              <Trans i18nKey="general_noUploadedDocument_errorMessage" />
            </WarningText>
          ) : null}
          <FormRow>
            <AsyncButton
              color={ButtonColor.Primary}
              onClick={handleContinue}
              size={ButtonSize.Medium}
              text={t('general_continue_button')}
              type="button"
            />
          </FormRow>
        </>
      ) : null}
    </Container>
  );
};

export default CardReissueIssue;
